import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckCircle2, AlertTriangle, XCircle, Wrench, Target, Trophy } from 'lucide-react';
import { SecurityFinding } from '@/types';

// Best practices / improvement points for each category to achieve 10/10
const CATEGORY_IMPROVEMENT_POINTS: Record<string, string[]> = {
  authentication: [
    'Implement Multi-Factor Authentication (MFA) for all user accounts',
    'Use secure password hashing (bcrypt, Argon2) with proper salt',
    'Implement account lockout after failed login attempts',
    'Add brute-force protection with rate limiting on login endpoints',
    'Use secure session management with proper timeout policies',
    'Implement password complexity requirements and breach detection',
  ],
  authorization: [
    'Implement Role-Based Access Control (RBAC) or Attribute-Based Access Control (ABAC)',
    'Apply principle of least privilege across all resources',
    'Add authorization checks at every API endpoint',
    'Implement resource-level permissions validation',
    'Use centralized authorization policy management',
    'Add audit logging for all authorization decisions',
  ],
  encryption: [
    'Use TLS 1.3 for all data in transit',
    'Encrypt sensitive data at rest using AES-256',
    'Implement proper key management with HSM or KMS',
    'Use secure cryptographic libraries (avoid deprecated algorithms)',
    'Implement certificate pinning for mobile/API clients',
    'Enable Perfect Forward Secrecy (PFS) for TLS connections',
  ],
  inputValidation: [
    'Implement server-side input validation for all user inputs',
    'Use parameterized queries to prevent SQL injection',
    'Sanitize HTML output to prevent XSS attacks',
    'Validate file uploads (type, size, content)',
    'Implement Content Security Policy (CSP) headers',
    'Use allowlist validation instead of blocklist',
  ],
  logging: [
    'Log all security-relevant events (auth, access, errors)',
    'Implement centralized log aggregation (ELK, Splunk)',
    'Add correlation IDs for request tracing',
    'Configure log retention policies per compliance requirements',
    'Implement real-time alerting for security events',
    'Ensure logs do not contain sensitive data (PII, secrets)',
  ],
  dependencyManagement: [
    'Use automated dependency scanning (Snyk, Dependabot)',
    'Implement Software Bill of Materials (SBOM)',
    'Pin dependency versions and use lock files',
    'Regularly update dependencies with security patches',
    'Use private artifact repositories for internal packages',
    'Implement license compliance checking',
  ],
  networkSecurity: [
    'Implement Web Application Firewall (WAF)',
    'Configure proper CORS policies (no wildcards)',
    'Use network segmentation and VPCs',
    'Implement DDoS protection',
    'Configure security headers (HSTS, X-Frame-Options, etc.)',
    'Use private subnets for backend services',
  ],
  secretsManagement: [
    'Use centralized secrets manager (HashiCorp Vault, AWS Secrets Manager)',
    'Implement automated secrets rotation (90-day policy)',
    'Enable audit logging for all secrets access',
    'Use short-lived credentials and tokens where possible',
    'Implement secrets scanning in CI/CD pipeline',
    'Never commit secrets to version control',
  ],
  aiSecurity: [
    'Implement input sanitization before AI/LLM API calls',
    'Add output validation and filtering for AI responses',
    'Implement rate limiting on AI endpoints',
    'Add guardrails to prevent prompt injection attacks',
    'Monitor and log AI model inputs/outputs for abuse',
    'Implement data leakage prevention for sensitive information',
  ],
  apiSecurity: [
    'Implement BOLA/IDOR protection with proper authorization',
    'Add rate limiting per user/IP on all endpoints',
    'Prevent mass assignment vulnerabilities',
    'Implement SSRF protection for external requests',
    'Use API versioning and deprecation policies',
    'Add request/response validation with OpenAPI schemas',
  ],
  cloudSecurity: [
    'Implement least-privilege IAM policies',
    'Enable cloud security posture management (CSPM)',
    'Use immutable infrastructure patterns',
    'Configure secure storage bucket policies (no public access)',
    'Implement container security scanning',
    'Enable cloud audit logging (CloudTrail, etc.)',
  ],
  cicdSecurity: [
    'Implement secrets management in CI/CD (no hardcoded secrets)',
    'Add SAST/DAST scanning in pipeline',
    'Use signed commits and protected branches',
    'Implement supply chain security (signed artifacts)',
    'Add infrastructure-as-code security scanning',
    'Use ephemeral build environments',
  ],
  businessLogic: [
    'Implement race condition protection (optimistic locking)',
    'Add proper session management and timeout',
    'Validate business workflow state transitions',
    'Implement WebSocket security (authentication, rate limiting)',
    'Add transaction integrity checks',
    'Implement idempotency for critical operations',
  ],
  packageLibrarySecurity: [
    'Scan both direct and transitive dependencies with an SCA tool (Snyk, OWASP Dependency-Check)',
    'Enable automated dependency update PRs (Dependabot, Renovate)',
    'Pin all dependency versions in lock files (package-lock.json, poetry.lock, Cargo.lock, etc.)',
    'Override vulnerable transitive dependency versions explicitly in dependencyManagement/resolutions',
    'Monitor CISA KEV for actively exploited CVEs in your dependency tree',
    'Generate and maintain a Software Bill of Materials (SBOM) using CycloneDX or SPDX',
  ],
};

interface CategoryRecommendationsDialogProps {
  open: boolean;
  onClose: () => void;
  categoryKey: string;
  categoryDisplayName: string;
  score: number;
  findings: SecurityFinding[];
}

export function CategoryRecommendationsDialog({
  open,
  onClose,
  categoryKey,
  categoryDisplayName,
  score,
  findings,
}: CategoryRecommendationsDialogProps) {
  const theme = useTheme();

  const scoreColor = (v: number) =>
    v >= 90 ? '#22c55e' : v >= 70 ? '#3b82f6' : v >= 50 ? '#f59e0b' : v >= 25 ? '#f97316' : '#ef4444';

  // Filter findings for this category (non-info severity only)
  // f.category is already normalized to canonical key by SecurityScoreCard
  const categoryFindings = findings.filter((f) => {
    return f.category === categoryKey && f.severity !== 'info';
  });

  // Check if no actionable findings
  const hasIssues = categoryFindings.length > 0;
  const isPerfectScore = score >= 100;

  // Get improvement points for this category
  const improvementPoints = CATEGORY_IMPROVEMENT_POINTS[categoryKey] || [];

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle size={16} color="#dc2626" />;
      case 'high':
        return <XCircle size={16} color="#ef4444" />;
      case 'medium':
        return <AlertTriangle size={16} color="#f59e0b" />;
      case 'low':
        return <AlertTriangle size={16} color="#22c55e" />;
      default:
        return null;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#22c55e';
      default: return theme.palette.grey[500];
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            {categoryDisplayName}
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(scoreColor(score), 0.1),
              border: `1px solid ${alpha(scoreColor(score), 0.3)}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: scoreColor(score),
              }}
            >
              {score}/100
            </Typography>
          </Box>
        </Stack>
        {/* Score bar */}
        <Box
          sx={{
            mt: 1.5,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.divider, 0.2),
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${score}%`,
              height: '100%',
              borderRadius: 3,
              bgcolor: scoreColor(score),
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, maxHeight: 400, overflow: 'auto' }}>
        {/* Perfect Score - 10/10 */}
        {isPerfectScore ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Trophy size={48} color="#22c55e" />
            <Typography
              sx={{
                mt: 2,
                fontSize: 16,
                fontWeight: 600,
                color: '#22c55e',
              }}
            >
              Perfect Score!
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontSize: 13,
                color: 'text.secondary',
              }}
            >
              All security best practices are implemented for this category.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Section 1: Issues to Fix (if any findings) */}
            {hasIssues && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Wrench size={18} color={theme.palette.error.main} />
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    Issues to Fix
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  {categoryFindings.map((finding, index) => (
                    <Box
                      key={finding.id || index}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(getSeverityColor(finding.severity), 0.05),
                        border: `1px solid ${alpha(getSeverityColor(finding.severity), 0.2)}`,
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                        <Box sx={{ mt: 0.25 }}>{getSeverityIcon(finding.severity)}</Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: 'text.primary',
                              lineHeight: 1.4,
                            }}
                          >
                            {finding.title || finding.finding || finding.description || 'Unnamed issue'}
                          </Typography>

                          {finding.description && (finding.title || finding.finding) && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                lineHeight: 1.5,
                                mt: 0.5,
                              }}
                            >
                              {finding.description}
                            </Typography>
                          )}

                          {(() => {
                            const recs = finding.recommendations && finding.recommendations.length > 0
                              ? finding.recommendations
                              : finding.recommendation
                                ? [finding.recommendation]
                                : [];
                            return recs.length > 0 ? (
                            <Box sx={{ mt: 1.5, pl: 1 }}>
                              {recs.map((rec, recIndex) => (
                                <Stack
                                  key={recIndex}
                                  direction="row"
                                  spacing={1}
                                  sx={{ mb: 0.75 }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: theme.palette.primary.main,
                                      fontWeight: 500,
                                    }}
                                  >
                                    •
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: 'text.secondary',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {rec}
                                  </Typography>
                                </Stack>
                              ))}
                            </Box>
                          ) : null;
                          })()}
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Section 2: To Achieve 100/100 (improvement points) */}
            {improvementPoints.length > 0 && (
              <Box>
                {/* Show "No critical issues" message if no actionable findings */}
                {!hasIssues && (
                  <Stack 
                    direction="row" 
                    alignItems="center" 
                    spacing={1} 
                    sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: alpha('#22c55e', 0.1),
                      border: `1px solid ${alpha('#22c55e', 0.2)}`,
                    }}
                  >
                    <CheckCircle2 size={18} color="#22c55e" />
                    <Typography sx={{ fontSize: 13, color: '#22c55e', fontWeight: 500 }}>
                      No critical issues found
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Target size={18} color={theme.palette.info.main} />
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    To Achieve 100/100
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                  }}
                >
                  {improvementPoints.map((point, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      spacing={1.5}
                      sx={{ mb: index < improvementPoints.length - 1 ? 1.25 : 0 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.info.main,
                          fontWeight: 600,
                        }}
                      >
                        •
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: 'text.secondary',
                          lineHeight: 1.5,
                        }}
                      >
                        {point}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
